/**
 * InvisiblePresenceCue
 * Plays a subtle cue when an invisible enemy is nearby.
 * - Checks nearest invisible opponent each update.
 * - Fires a one-shot sound with distance-based volume.
 * - Enforces a cooldown between cues.
 */
import { resolveSound } from '../../shared/resolveSound.js';

class InvisiblePresenceCue {
  constructor({ gameState, spatialAudio }) {
    this.gameState = gameState;
    this.players = gameState?.players;
    this.spatialAudio = spatialAudio;

    this.soundPath = null;
    this.baseVolume = 0.25;
    this.minVolume = 0.05;
    this.radius = 450;
    this.cooldownMs = 3000;

    this.lastCueAt = 0;
    this.isPlaying = false;
    this.enabled = false;
  }

  setSoundConfig(soundConfig) {
    const resolved = resolveSound(soundConfig);
    if (!resolved) {
      this.enabled = false;
      this.soundPath = null;
      return;
    }

    this.soundPath = resolved.path;
    this.baseVolume = resolved.volume;
    this.radius = soundConfig.radius || 450;
    this.cooldownMs = soundConfig.cooldownMs || 3000;
    this.minVolume = typeof soundConfig.minVolume === 'number' ? soundConfig.minVolume : 0.05;
    this.enabled = true;
  }

  update(localPlayer) {
    if (!this.enabled || !localPlayer || !this.players) {
      return;
    }

    const now = performance.now();
    if (this.isPlaying || (now - this.lastCueAt) < this.cooldownMs) {
      return;
    }

    const nearest = this._findNearestInvisible(localPlayer);
    if (!nearest) {
      return;
    }

    const distanceRatio = Math.min(1, nearest.distance / Math.max(1, this.radius));
    const volume = Math.max(this.minVolume, this.baseVolume * (1 - distanceRatio));
    if (volume <= 0) {
      return;
    }

    this._play(volume, now);
  }

  _findNearestInvisible(localPlayer) {
    const localId = this.gameState?.localId;
    let closestDistance = this.radius;
    let nearest = null;

    for (const [playerId, player] of this.players.entries()) {
      if (!player || playerId === localId) continue;
      if (player.isDead) continue;
      const effects = player.activeAbilityEffects;
      if (!effects || !effects.invisibility) continue;

      const dx = (player.x || 0) - (localPlayer.x || 0);
      const dy = (player.y || 0) - (localPlayer.y || 0);
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance < closestDistance) {
        closestDistance = distance;
        nearest = { distance };
      }
    }

    return nearest;
  }

  async _play(volume, now) {
    if (!this.soundPath || !this.spatialAudio) {
      return;
    }

    this.isPlaying = true;

    try {
      const source = await this.spatialAudio.playLocal(this.soundPath, { volume });
      if (!source) {
        this.isPlaying = false;
        return;
      }

      this.lastCueAt = now;
      source.onended = () => {
        this.isPlaying = false;
      };
    } catch (err) {
      this.isPlaying = false;
    }
  }
}

export { InvisiblePresenceCue };
